<template>
  <div class="common-dialog">
    <el-dialog
      class="custom-dialog"
      :visible.sync="visible"
      :width="width + '%'"
      :close-on-click-modal="false"
    >
      <div slot="title" class="header">
        <span class="title">{{ title }}</span>
        <div class="header-ops">
          <slot name="buttons"></slot>
          <div class="op confirm" @click="save">
            <svg-icon icon-class="check" class="op-icon" />
            <span>{{ okText }}</span>
          </div>
          <div class="op clear" @click="reset" v-if="showResetBtn">
            <svg-icon icon-class="reset" class="op-icon" />
            <span>Reset</span>
          </div>
          <div class="op close" @click="close">
            <svg-icon icon-class="close" class="op-icon" />
            <span>Close</span>
          </div>
        </div>
      </div>
      <div class="error-msg" v-if="errorMsg !== ''">
        <el-alert :title="errorMsg" type="error" :closable="false" />
      </div>
      <slot name="content"></slot>
      <div
        class="display-item f-r-hsb"
        v-for="(item, index) in displayMap"
        :key="'dialog-display-' + index"
      >
        <span class="label">{{ item.label }}</span>
        <span>{{ item.value }}</span>
      </div>
      <div
        class="input-item f-r-c-sb"
        v-for="(input, index) in inputsMap"
        :key="'dialog-input-' + index"
      >
        <span class="label">{{ input.label }}</span>
        <div class="f-c">
          <el-input
            v-if="input.type === T.TextInput"
            size="medium"
            v-model="input.value"
            @change="res => valueChange(input.key, res)"
          />
          <el-input-number
            v-else-if="input.type === T.NumberInput"
            size="medium"
            v-model="input.value"
            placeholder="0"
            :min="input.min"
            @change="res => valueChange(input.key, res)"
          >
          </el-input-number>
          <el-input
            v-else-if="input.type === T.TextArea"
            :autosize="{
              minRows: input.minRows || 2,
              maxRows: input.maxRows || 5
            }"
            size="medium"
            type="textarea"
            v-model="input.value"
            @change="res => valueChange(input.key, res)"
          />
          <el-select
            size="medium"
            v-else-if="input.type === T.Select"
            v-model="input.value"
            placeholder="Select"
            @change="res => valueChange(input.key, res)"
          >
            <el-option
              v-for="item in input.options"
              :key="item"
              :label="item"
              :value="item"
            >
            </el-option>
          </el-select>
          <address-pick
            size="medium"
            v-else-if="input.type === T.AddressSelect"
            v-model="input.value"
            @change="val => valueChange(input.key, val)"
          />
          <stock-group-pick
            v-else-if="input.type === T.StockGroupSelect"
            v-model="input.value"
            :groups="input.options"
            @input="val => valueChange(input.key, val)"
          />
          <el-switch
            v-else-if="input.type === T.HoldSelect"
            v-model="input.value"
            active-text="Hold"
            inactive-text="Not Hold"
            :active-value="HOLD"
            :inactive-value="UNHOLD"
          />
          <address-tag
            :address="input.value"
            v-else-if="input.type === T.AddressTag"
          />
          <el-select
            size="medium"
            remote
            v-else-if="input.type === T.RemoteSelect"
            v-model="input.value"
            filterable
            clearable
            :loading="input.loading"
            placeholder="input to search"
            :remote-method="input.method.bind(input)"
            @change="res => valueChange(input.key, res)"
          >
            <el-option
              v-for="item in input.options"
              :key="item.key"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <el-checkbox
            v-else-if="input.type === T.Checkbox"
            v-model="input.value"
            @change="res => valueChange(input.key, res)"
          >
            {{ input.activeText }}
          </el-checkbox>
          <el-switch
            v-else-if="input.type === T.Switch"
            v-model="input.value"
            :active-text="input.activeText"
            :inactive-text="input.inactiveText"
            @change="res => valueChange(input.key, res)"
          />
          <el-date-picker
            size="medium"
            v-else-if="input.type === T.TimeSelect"
            v-model="input.value"
            type="datetime"
            placeholder="Select date and time"
            @change="res => valueChange(input.key, res)"
          />
          <el-date-picker
            size="small"
            v-else-if="input.type === T.DateSelect"
            v-model="input.value"
            type="date"
            placeholder="Select date and time"
            @change="res => valueChange(input.key, res)"
          />
          <div v-else>{{ input.value }}</div>
          <span v-if="input.key in inputValueErrors" class="error-hint">
            {{ inputValueErrors[input.key] }}
          </span>
        </div>
      </div>
      <div slot="footer" class="dialog-footer" v-if="hasFooter">
        <slot name="footer"> </slot>
        <el-button type="primary" size="small" @click="save">
          Confirm
        </el-button>
        <el-button type="danger" size="small" @click="close">
          Cancel
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import * as T from '@/core/configs/types'
import { HOLD, UNHOLD } from '@/core/models/constant'
import AddressPick from '../address/AddressPick.vue'
import AddressTag from '@/components/address/AddressTag.vue'
import StockGroupPick from '../shipment/StockGroupSelect.vue'
export default {
  name: 'CommonDialog',
  components: { AddressPick, AddressTag, StockGroupPick },
  props: {
    title: {
      type: String
    },
    width: {
      type: Number,
      default: 30
    },
    inputsMap: {
      type: Array,
      default: () => []
    },
    resetInputs: {
      type: Function,
      default: () => {}
    },
    saveCb: {
      type: Function,
      default: () => [true, '']
    },
    okText: {
      type: String,
      default: 'Confirm'
    },
    displayMap: {
      type: Array,
      default: () => []
    },
    hasFooter: {
      type: Boolean,
      default: false
    },
    showResetBtn: {
      type: Boolean,
      default: true
    },
    inputValueChange: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      T,
      HOLD,
      UNHOLD,
      DateSelect: T.DateSelect,
      visible: false,
      errorMsg: '',
      inputValueErrors: {}
    }
  },
  methods: {
    open() {
      this.inputsMap.forEach(item => {
        if (item.type === T.RemoteSelect) {
          item.method()
        }
      })
      this.reset()
      this.visible = true
    },
    save() {
      this.errorMsg = ''
      const res = this.validateAllInputs()
      if (!res[0]) return
      this.saveCb(res[1], this.close, this.error)
    },
    close() {
      this.reset()
      this.visible = false
    },
    error(msg) {
      if (typeof msg !== 'string') msg = msg.toString()
      this.errorMsg = msg
    },
    reset() {
      this.errorMsg = ''
      this.resetInputs()
      this.resetErrors()
    },
    validateKeyValue(keyItem, value) {
      const { validators } = keyItem
      let [i, passed, msg] = [0, true, '']
      while (passed && i < validators.length) {
        ;[passed, msg] = validators[i].call(keyItem, value)
        i++
      }
      if (!passed) {
        this.$set(this.inputValueErrors, keyItem.key, msg)
      } else {
        this.$delete(this.inputValueErrors, keyItem.key)
      }
      return [passed, msg]
    },
    validateAllInputs() {
      const errs = []
      let [passed] = [true]
      const values = {}
      this.inputsMap
        .filter(item => T.EditFormItemTypes.includes(item.type))
        .forEach(item => {
          ;[passed] = this.validateKeyValue(item, item.value)
          if (!passed) errs.push(item.key)
          if (item.value && item.convertor) {
            values[item.key] = item.convertor(item.value)
          } else values[item.key] = item.value
        })
      return [errs.length === 0, values]
    },
    resetErrors() {
      this.inputValueErrors = {}
    },
    valueChange(key, value) {
      this.inputValueChange(key, value)
    }
  }
}
</script>
<style lang="scss">
@import '@/assets/styles/dialog.scss';
</style>
<style scoped lang="scss">
@import '@/assets/styles/common.scss';

.input-item {
  padding: 0 1px;
  margin-bottom: 10px;

  div {
    span {
      font-size: 11px;
      color: $el-danger-color;
    }
    width: 60%;
  }

  .label {
    max-width: 40%;
    word-break: break-word;
  }

  .el-select,
  .el-input,
  .el-textarea,
  .el-switch,
  .el-input-number {
    width: 100%;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
}

.header {
  .title {
    font-size: $title-level-three;
    font-weight: bold;
  }

  .header-ops {
    gap: 10px;

    @extend .f-r;

    .op {
      gap: 5px;

      &:hover {
        cursor: pointer;
      }

      @extend .f-r-vc;

      span {
        font-size: $title-level-regular;
      }

      .op-icon {
        font-size: $dialog-header-op-icon-size;
        user-select: none;
      }
    }

    .confirm .op-icon {
      filter: $svg-business-color;
    }

    .close .op-icon {
      filter: $svg-danger-color;
    }

    .clear .op-icon {
      filter: $svg-warning-color;
      font-size: calc($dialog-header-op-icon-size - 2px);
    }
  }

  @extend .f-r-c-sb;
}

.error-msg {
  margin-bottom: 10px;
  word-break: break-word;
}

.display-item {
  padding: 0 1px;
  margin-bottom: 5px;
}
</style>
